/**
 * Risk Cluster panel: surfaces EXPIRATION_CLUSTER_RISK and similar risk-type
 * insights prominently so the operator knows what needs attention next.
 * Clear, non-punitive language.
 */
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardBody } from '../ui';
import { AlertTriangle, FileText, Calendar } from 'lucide-react';
import { formatAnalyticsDocumentDisplay } from '../../utils/analyticsDocumentLabel';

const RISK_INSIGHT_TYPES = [
  'expiration_cluster_risk',
  'expired_document',
  'expiring_document',
  'document_hygiene_declining',
  'authority_mcs150_due',
  'vehicle_registration_due',
  'missing_required_document',
];

function getRiskCta(insight) {
  const type = (insight.insightType || '').toLowerCase();
  if (type.includes('document') || type.includes('expir')) return { label: 'Go to documents', path: '/documents', icon: FileText };
  if (type.includes('authority') || type.includes('mcs150')) return { label: 'Go to authority', path: '/authority', icon: Calendar };
  if (type.includes('vehicle') || type.includes('registration')) return { label: 'Go to vehicles', path: '/vehicles', icon: FileText };
  return { label: 'View timeline', path: '/timeline', icon: AlertTriangle };
}

export default function RiskClusterPanel({ insights = [], loading }) {
  const navigate = useNavigate();
  const riskInsights = useMemo(
    () => (insights || []).filter((i) => RISK_INSIGHT_TYPES.includes((i.insightType || '').toLowerCase())),
    [insights],
  );

  if (loading) {
    return (
      <Card className="analytics-card risk-cluster-panel">
        <CardBody>
          <div className="analytics-card-loading">
            <div className="loading-spinner" />
            <span>Checking what needs attention...</span>
          </div>
        </CardBody>
      </Card>
    );
  }

  if (riskInsights.length === 0) {
    return (
      <Card className="analytics-card risk-cluster-panel">
        <CardHeader title="What needs attention" subtitle="Open items and deadlines" />
        <CardBody>
          <div className="risk-cluster-empty">
            <AlertTriangle size={32} style={{ color: 'var(--success)' }} />
            <p>No priority clusters right now. Keep renewals and updates on schedule to maintain this position.</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="analytics-card risk-cluster-panel">
      <CardHeader
        title="What needs attention"
        subtitle={`${riskInsights.length} item${riskInsights.length === 1 ? '' : 's'} to address`}
      />
      <CardBody>
        <ul className="risk-cluster-list">
          {riskInsights.map((i) => {
            const cta = getRiskCta(i);
            const Icon = cta.icon;
            return (
              <li key={i.id} className="risk-cluster-item">
                <div className="risk-cluster-item-content">
                  <span className="risk-cluster-item-title">{formatAnalyticsDocumentDisplay(i.title)}</span>
                  {i.description && (
                    <span className="risk-cluster-item-desc">{formatAnalyticsDocumentDisplay(i.description)}</span>
                  )}
                </div>
                <button
                  type="button"
                  className="risk-cluster-cta"
                  onClick={() => navigate(cta.path)}
                >
                  <Icon size={16} />
                  {cta.label}
                </button>
              </li>
            );
          })}
        </ul>
      </CardBody>
    </Card>
  );
}
